import { Button, Checkbox, ComboboxItem, Grid, Group, Select, Text, TextInput, useMantineTheme } from '@mantine/core';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPono } from '../../api/poList';
import AllocationTable from '../../components/AllocationTable/AllocationTable';
import InvoiceTable from '../../components/InvoiceTable/InvoiceTable';
import NavBar from '../../components/NavBar/NavBar';
import StyleTable from '../../components/TransitTable/StyleTable';
import { STORES, STORES_PAIRS } from '../../constants/stores';
import { useDebounce } from '../../Hooks/useDebounce';
import { PoNoWithStatus } from '../../interface/AllocationInvoice';
import { StoreData } from '../../interface/AllocationTable';
import { StyleTableData } from '../../interface/TransitTable';
import { allocationPaperPDF } from '../../PDFs/allocationPaper';
import { sizeAllocationPDF } from '../../PDFs/sizeAllocation';
import { FEATURE_FLAGS } from '../../Utils/utils';
import classes from './Allocation.module.css';

const RestockCalculator: React.FC = () => {
    const theme = useMantineTheme();
    const token = useSelector((state: any) => state.user.sessionId);
    const [poNoList, setPoNoList] = useState<PoNoWithStatus[]>([]);
    const [po, setPo] = useState<{ poNo: string, poId: string }>();
    const [stores, setStores] = useState<{ store: string, checked: boolean }[]>(STORES.map((store: string) => ({ store: store, checked: true })));
    const [sizes, setSizes] = useState<string[]>([]);
    const [totalProductionQuantities, setTotalProductionQuantities] = useState<number[]>([]);
    const [standardQuantities, setStandardQuantities] = useState<number[]>([]);
    const [totalProductionSum, setTotalProductionSum] = useState<number>(0);
    const [calculateClicked, setCalculateClicked] = useState(false);
    const [tableData, setTableData] = useState<StoreData[]>([]);
    const [calculateBtnClicked, setCalculateBtnClicked] = useState(false);
    const [message, setMessage] = useState('');
    const [poTableData, setPoTableData] = useState<any>(null);
    const [supplierList, setSupplierList] = useState<{ label: string, value: string }[]>([]);
    const [poSearchTerm, setPoSeachTerm] = useState('');
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const poSearch = useDebounce(poSearchTerm, 500);
    const supplierSearch = useDebounce(supplierSearchTerm, 500);
    const [styleTableData, setStyleTableData] = useState<StyleTableData[]>([]);
    const [styleMessage, setStyleMessage] = useState('');
    const [isStyleLoading, setIsStyleLoading] = useState(false);
    const [prePacked, setPrePacked] = useState(false);
    const [packed, setPacked] = useState<number[]>([]);
    const [requiredAdjustment, setRequiredAdjustment] = useState<any>()
    const [osBox, setOsBox] = useState(false);
    const [styleNo, setStyleNo] = useState('');
    const [osBoxPacked, setOsBoxPacked] = useState<number[]>([]);
    const initialAllocation = {
        poNo: '',
        poId: '',
        supplierName: '',
        styleNo: '',
        cost: '',
        msrp: '',
        notes: '',
        binLocation: '',
        description: '',
        color: ''
    }
    const [allocation, setAllocation] = useState<
        {
            poNo: string,
            poId: string,
            supplierName: string,
            styleNo: string,
            cost: string,
            msrp: string,
            notes: string,
            binLocation: string,
            description: string,
            color: string
        }>(initialAllocation);

    const getTableData = async (poId: string) => {
        try {
            const response = await fetch('/po/poDetails', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ poId: poId }),
            });
            if (response.status !== 200) {
                setPoTableData(null);
            }
            const data = await response.json();
            setPoTableData(data);
        } catch (error) {
            console.log('Table data fetch error:', error);
        }
    }

    const getStyle = async (styleNo: string, event?: any) => {
        if (!styleNo || styleNo === '') return;
        try {
            setIsStyleLoading(true);
            let url = `/style/rms/styleInfo?style_no=${styleNo}`;
            if (po?.poNo) {
                url += `&po_no=${encodeURIComponent(po.poNo)}`;
            }
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const { data, message } = await response.json();
            if (response.status === 200) {
                setAllocation(prev => ({
                    ...prev,
                    color: data.style.color?.toUpperCase() || '',
                    description: data.style.description?.toUpperCase() || '',
                    binLocation: data.style.binLocation?.toUpperCase() || '',
                }));
                setSizes(data.style.sizes);
                setTotalProductionQuantities(data.style.quantities);
                if (data.style.message) {
                    setStyleMessage(data.style.message);
                } else {
                    setStyleMessage('');
                }
                setStyleTableData(data.styleTable);
                if (osBox) setOsBoxPacked(new Array(sizes.length).fill(0));
            }
            if (response.status === 201) {
                // await setStyleTableData([]);
            }
            if (response.status === 400 && event) {
                if (data) {
                    setSizes(data.style.sizes);
                    setStyleTableData(data.styleTable);
                    setAllocation(prev => ({
                        ...prev,
                        color: data.style.color?.toUpperCase() || allocation.color,
                        description: data.style.description?.toUpperCase() || allocation.description,
                        binLocation: data.style.binLocation?.toUpperCase() || allocation.binLocation,
                    }));
                    setTotalProductionQuantities(data.style.quantities);
                    if (osBox) setOsBoxPacked(new Array(sizes.length).fill(0));
                }
                event.target.focus();
                event.preventDefault();
                alert(message);
                setAllocation(prev => ({ ...prev, styleNo: '' }));
                getTableData(po?.poId || '');
            }
            setIsStyleLoading(false);
            return true;
        } catch (error) {
            console.log('Error getting style data:', error);
            setIsStyleLoading(false);
            return false;
        }
    };
    const updatedTableData = (tableData: any, styleTableData: any) => {
        const updatedData = tableData.map((storeEntry: any) => {
            const matchingStore = storeEntry.storeName === 'SVP NM' ? styleTableData.find((s: any) => s.store.toUpperCase() === 'NEWMARKET') :
                storeEntry.storeName === 'SC SuperStore' ? styleTableData.find((s: any) => s.store.toUpperCase() === 'NEW-SCARBOROUGH') :
                    styleTableData.find((s: any) => s.store.toUpperCase() === storeEntry.storeName.toUpperCase());

            if (!matchingStore || !Array.isArray(matchingStore.storeData)) {
                return storeEntry; // No match, return original
            }

            const updatedSizeQuantities = storeEntry.sizeQuantities.map((sizeEntry: any) => {
                const match = matchingStore.storeData.find(
                    (s: any) => s.size === sizeEntry.size
                );

                const updatedQuantity = match
                    ? (match.qty || 0) + (match.inTransit || 0)
                    : 0;

                return {
                    ...sizeEntry,
                    quantity: updatedQuantity
                };
            });

            return {
                ...storeEntry,
                sizeQuantities: updatedSizeQuantities
            };
        })
        setTableData(updatedData);
    }
    const getPonoData = async (searchTerm?: string) => {
        try {
            const response = await getPono(token, searchTerm || '');
            const data = response?.data;
            if (data === null) {
                setPoNoList([{ poNo: `+ Create ${searchTerm?.toUpperCase() || ''}` }]);
                return;
            }
            setPoNoList([...data, { poNo: `+ Create ${searchTerm?.toUpperCase() || ''}` }]);
        } catch (error) {
            console.log(error);
        }
    }

    const getSuppliers = async (searchTerm?: string) => {
        if (!searchTerm || searchTerm === '') return;
        try {
            const response = await fetch(`/supplier/getAllsuppliers?searchTerm=${searchTerm}&limit=5&page=1`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            if (response.status === 204 || response.status === 304) {
                setSupplierList(prev => [
                    ...prev,
                    {
                        label: `+ Create ${searchTerm?.toUpperCase() || ''}`,
                        value: `+ Create ${searchTerm?.toUpperCase() || ''}`
                    }
                ]);
                return;
            }
            if (response.status === 200) {
                const { data, message } = await response.json();
                setSupplierList(data.map((item: any) => ({ label: item.supplier_name, value: item.supplier_name })));
            }
        } catch (error) {
            console.log('Error getting suppliers:', error)
        }
    }

    useEffect(() => {
        getPonoData(poSearchTerm);
    }, [poSearch]);

    useEffect(() => {
        getSuppliers(supplierSearchTerm);
    }, [supplierSearch])

    const createSupplier = async (supplierName: string) => {
        try {
            const response = await fetch('/supplier/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ supplierName })
            });
            const { data, message } = await response.json();

            if (response.status === 200) {
                setSupplierList(prev => [
                    ...prev,
                    { label: data, value: data }
                ]);
                setAllocation(prev => ({
                    ...prev,
                    supplierName: data
                }));
            }
        } catch (error) {
            console.log('Error creating supplier:', error);
            return null;
        }
    }

    const handlePoNoChange = (value: string | null, option: ComboboxItem) => {
        if (value?.startsWith('+ Create ')) {
            const poNumber = value.split('+ Create ')[1].toUpperCase() ?? '';
            //check if po number alredy exists in the poNoList
            if (poNoList.some((item: any) => item.poNo === poNumber)) {
                // set the existing po number and do not add it to the poNoList
                setPo({ poNo: poNumber, poId: poNoList.find((item: any) => item.poNo === poNumber)?.poId?.toString() || '' })
                return;
            } else {
                setPo({ poNo: poNumber, poId: '' });
                setPoNoList(prev => [...prev, { poNo: poNumber, poId: 0 }]);
            }
        } else {
            let selectedPo = { poNo: value ?? '', poId: poNoList.find((item) => item.poNo === value)?.poId?.toString() || '' }
            setPo(selectedPo);
            getTableData(selectedPo.poId);
        }
    }

    const handleSupplierChange = async (value: string | null, option: ComboboxItem) => {
        if (value?.startsWith('+ Create ')) {
            let supplier = value.split('+ Create ')[1].toUpperCase() ?? '';
            await createSupplier(supplier);
        } else {
            setAllocation(prev => ({
                ...prev,
                supplierName: value || ''
            }));
        }
    }

    const handleSearchChange = (value: string) => {
        setPoSeachTerm(value.toUpperCase());
    }
    const handleSupplierSearch = (value: string) => {
        setSupplierSearchTerm(value.toUpperCase());
    }

    const handleStyleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const style_no = event.target.value?.trim().toUpperCase();
        // await getStyle(style_no, event);
        if (poTableData?.styles?.length > 0) {
            if (poTableData?.styles?.includes(style_no)) {
                event.target.focus();
                event.preventDefault();
                setAllocation(prev => ({
                    ...prev,
                    styleNo: ''
                }));
                alert('Style already exists in this PO. Please check the style number again.');
                return;
            }
        }
        if (styleNo !== style_no) {
            getStyle(style_no, event);
        }
        setStyleNo(style_no);
    };

    const handleReset = async (skipSaveConfirmation = false) => {
        const confirmReset = window.confirm("Do you want to reset the form?");
        if (confirmReset) {
            setTableData([]);
            setAllocation(prev => ({
                ...prev,
                styleNo: '',
                notes: '',
                description: '',
                binLocation: ''
            }));
            setTotalProductionQuantities([]);
            setStores(STORES.map((store: string) => ({ store: store, checked: false })));
            setStandardQuantities(Array(STORES.length).fill(0));
            setSizes([]);
            setStyleTableData([]);
            return;
        } else {
            return;
        }
    };

    const handleCheckAll = (checked: boolean) => {
        if (checked) {
            setStores(STORES.map((store: string) => ({ store: store, checked: true })));
        } else {
            setStores(STORES.map((store: string) => ({ store: store, checked: false })));
        }
    };

    const handleSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toUpperCase();
        let newSizes = value.split(',');
        setSizes(value ? newSizes : []);
        if (packed) {
            // Set packed to an array of 0s with the same length as newSizes
            setPacked(new Array(newSizes.length).fill(0));
        }
        if (osBox) {
            // Set packed to an array of 0s with the same length as newSizes
            setOsBoxPacked(new Array(newSizes.length).fill(0));
        }
        setTotalProductionQuantities(newSizes.map((size: string, index: number) => totalProductionQuantities[index] || 0));
    }


    const handleCalculateClick = () => {
        setCalculateBtnClicked(!calculateBtnClicked);
        setCalculateClicked(true);
    };

    const calculateTotalAllocation = (sizeIndex: number) => {
        return tableData.reduce(
            (total, row) => {
                // Check if row and row.sizeQuantities are not null
                if (row && row.sizeQuantities) {
                    // Check if sizeIndex is within the bounds of the sizeQuantities array
                    if (sizeIndex < row.sizeQuantities.length) {
                        return total + row.sizeQuantities[sizeIndex].quantity;
                    }
                }
                return total;
            },
            0
        );
    };

    const calculateOverstock = (sizeIndex: number) => {
        const totalAllocation = calculateTotalAllocation(sizeIndex);
        const totalProductionQuantity = totalProductionQuantities[sizeIndex] || 0;
        const result = Math.max(totalProductionQuantity - totalAllocation);
        return isNaN(result) ? 0 : result;
    };


    const onSave = async () => {
        let tempPoId = allocation?.poId;
        if (allocation?.poId === null || allocation?.poId === undefined) {
            setPo(prev => ({
                ...prev,
                poId: allocation?.poNo?.toUpperCase() || '',
                poNo: allocation?.poNo || ''
            }));
            tempPoId = allocation?.poId;
        }
        if (!allocation?.supplierName ||
            !allocation?.styleNo ||
            !allocation?.cost ||
            !allocation?.msrp ||
            allocation?.supplierName.trim() === '' ||
            allocation?.styleNo.trim() === '' ||
            allocation?.cost.trim() === '' ||
            allocation?.msrp.trim() === '' ||
            sizes.length === 0 ||
            totalProductionQuantities.length === 0
        ) {
            alert('Please fill in all required fields');
            return;
        }
        if (totalProductionQuantities.length !== sizes.length) {
            alert('Please enter appropriate quantity for each size');
            return;
        }
        const statusArray = Array(tableData.length).fill(false);
        const initialArray = Array(tableData.length).fill('SVP');
        const data = {
            poNo: po?.poNo,
            po_id: tempPoId,
            supplier_name: allocation?.supplierName,
            styleNo: allocation?.styleNo.trim(),
            description: allocation?.description,
            color: allocation?.color,
            cost: allocation?.cost,
            msrp: allocation?.msrp,
            notes: allocation?.notes,
            status: statusArray,
            binlocation: allocation?.binLocation?.toUpperCase() || '',
            storeName: tableData.map(data => data.storeName),
            sizeQuantities: tableData.map(data => data.sizeQuantities),
            receivedQty: sizes.map((size, index) => ({
                size: size,
                quantity: totalProductionQuantities[index],
            })),
            total: totalProductionQuantities.reduce((a, b) => a + b, 0),
            totalAllocationPerSize: sizes.map((_size, index) => calculateTotalAllocation(index)),
            overstockPerSize: sizes.map((_size, index) => calculateOverstock(index)),
            initial: initialArray,
            order_status: 'restock',
            // osCaseQty: osBoxPacked.map((qty: number, index: number) => {
            //     return {
            //         size: sizes[index],
            //         quantity: qty
            //     }
            // })
        };

        // const checkQty = data.totalAllocationPerSize.map((qty: number, index: number) => {
        //     const packSize = osBoxPacked[index];
        //     const remainder = qty % packSize;
        //     return {
        //         short: remainder,              // How much you'd remove to go down to full box
        //         needed: packSize - remainder     // How much you'd add to fill the box
        //     };
        // });
        // if (checkQty.some(qty => qty.short !== 0)) {
        //     setRequiredAdjustment(checkQty);
        //     const confirm = window.confirm('Allocated quantity is not matching with box size, do you want to continue?');
        //     if (!confirm) {
        //         return;
        //     }
        // }
        try {
            const response = await fetch('/allocation/create', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
            });
            const responseData = await response.json();
            if (response.status === 200) {
                getTableData(responseData.data.po_id || '');
                setPo({ poId: responseData.data.po_id, poNo: po?.poNo || '' });
                setRequiredAdjustment([]);
                alert('Data saved successfully');
                handleReset(true); // Pass true to skip save confirmation
            } else if (response.status === 400) {
                alert('Style No already exists');
                setMessage('Error while saving data');
            } else {
                alert(message ? message : 'Error while saving data');
            }
        } catch (error: any) {
            setMessage('Error while saving data: ' + error.message);
        }
    };
    function mergeStoreData(tableData: any[], storeData: Record<string, any>) {
        // Clone the storeData to avoid mutating the original
        const mergedData = { ...storeData };

        tableData.forEach(({ storeName, sizeQuantities }) => {
            const sumQuantities = sizeQuantities.reduce((sum: number, st: { quantity: number }) => sum + st.quantity, 0);

            if (mergedData[storeName]) {
                // If store exists, add to totalQuantity
                mergedData[storeName].totalStyles += 1;
                mergedData[storeName].totalQuantity += sumQuantities;
            } else {
                // If store doesn't exist, add a new entry
                mergedData[storeName] = {
                    totalStyles: 1,
                    totalQuantity: sumQuantities
                };
            }
        });

        return mergedData;
    }
    const downloadAllPDFs = async () => {
        if (sizes.length === 0 || tableData.length === 0 || totalProductionQuantities.length === 0) {
            alert('Please enter appropriate quantity for each size');
            return;
        }
        await allocationPaperPDF(
            sizes,
            tableData,
            totalProductionQuantities,
            totalProductionSum,
            allocation,
            po?.poNo || '',
            poTableData?.styles?.length + 1
        );
        await sizeAllocationPDF(sizes, 6, tableData, allocation.styleNo);
    }

    const handlePrePackChanges = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;
        if (!value) return;
        // set element at specific index
        setPacked((prev) => {
            const newPacked = [...prev];  // Create a copy of the previous packed array
            newPacked[index] = parseInt(value, 10);  // Set the value at the specified index
            return newPacked;  // Return the new array
        });
    }

    const handleOsBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) setOsBoxPacked(new Array(sizes.length).fill(0));
        setOsBox(e.target.checked);
    }

    const handleOsPackedBoxChanges = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;
        if (!value) return;
        // set element at specific index
        setOsBoxPacked((prev) => {
            const newPacked = [...prev];  // Create a copy of the previous packed array
            newPacked[index] = parseInt(value, 10);  // Set the value at the specified index
            return newPacked;  // Return the new array
        });
    }

    return (
        <div>
            <NavBar />
            <div className={classes.main}>
                <div>
                    <Grid>
                        <Grid.Col span={{ xs: 12, sm: 12, md: 3, xl: 3 }} className={classes.gridColDiv}>
                            <div style={{ marginTop: '30px', height: '35vh', overflow: 'auto', marginBottom: '10px', scrollbarWidth: 'none' }}>
                                <StyleTable styleData={styleTableData} style_no={styleNo} message={styleMessage} isStyleLoading={isStyleLoading} />
                            </div>
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 12, md: 6, xl: 6 }} className={classes.gridColDiv}>
                            <h2 className={'title'} style={{ marginTop: '30px' }}>Restock</h2>
                            <div className={classes.gridRowDiv} >
                                <Text className={classes.textWithInput}>Po No:</Text>
                                <Select
                                    data={Array.from({ length: poNoList.length }, (_, index) => poNoList[index].poNo)}
                                    placeholder="Select Po No"
                                    style={{ flexGrow: 1 }}
                                    value={po?.poNo || null}
                                    searchable
                                    clearable
                                    onChange={handlePoNoChange}
                                    onSearchChange={handleSearchChange}
                                />
                            </div>
                            <Grid>
                                <Grid.Col span={{ xs: 12, sm: 6, md: 6, xl: 6 }} className={classes.gridColDiv}>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Supplier:</Text>
                                        <Select
                                            data={supplierList}
                                            style={{ flexGrow: 1 }}
                                            searchable
                                            clearable
                                            value={allocation.supplierName}
                                            onChange={handleSupplierChange}
                                            onSearchChange={handleSupplierSearch}
                                        />
                                    </div>
                                    <div className={classes.gridRowDiv}>
                                        <Text className={classes.textWithInput}>Style No:</Text>
                                        <TextInput
                                            style={{ flexGrow: 1 }}
                                            value={allocation?.styleNo}
                                            onChange={(e) => setAllocation(prev => ({ ...prev, styleNo: e.target.value.toUpperCase() }))}
                                            onBlur={handleStyleChange}
                                        />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Description:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.description} onChange={(e) => setAllocation(prev => ({ ...prev, description: e.target.value.toUpperCase() }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Notes:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.notes} onChange={(e) => setAllocation(prev => ({ ...prev, notes: e.target.value.toUpperCase() }))} />
                                    </div>
                                </Grid.Col>
                                <Grid.Col span={{ xs: 12, sm: 6, md: 6, xl: 6 }} className={classes.gridColDiv}>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Color:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.color} onChange={(e) => setAllocation(prev => ({ ...prev, color: e.target.value.toUpperCase() }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>Cost:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.cost} onChange={(e) => setAllocation(prev => ({ ...prev, cost: e.target.value }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>MSRP:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.msrp} onChange={(e) => setAllocation(prev => ({ ...prev, msrp: e.target.value }))} />
                                    </div>
                                    <div className={classes.gridRowDiv} >
                                        <Text className={classes.textWithInput}>BIN:</Text>
                                        <TextInput style={{ flexGrow: 1 }} value={allocation?.binLocation} onChange={(e) => setAllocation(prev => ({ ...prev, binLocation: e.target.value }))} />
                                    </div>
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 12, md: 3, xl: 3 }} className={classes.gridColDiv}>
                            <div className={classes.gridColDiv} style={{ marginTop: '30px', height: '35vh', overflow: 'auto', marginBottom: '10px' }}>
                                <InvoiceTable data={poTableData} />
                            </div>
                        </Grid.Col>
                    </Grid>
                </div>
                <div className={classes.sizeStoreDiv} style={{ backgroundColor: theme.colors.gray[1], color: theme.colors.gray[10] }}>
                    <div className={classes.rowDiv} >
                        <Text className={classes.textWithInput}>Sizes:</Text>
                        <TextInput size='xs' style={{ flexGrow: 1 }} value={sizes} onChange={handleSizeChange} />
                        <Button onClick={() => updatedTableData(tableData, styleTableData)}>Get quantity</Button>
                    </div>
                    {/*Pre Packed*/}
                    {/* <div className={classes.rowDiv} >
                        <Text className={classes.textWithInput} style={{ width: '100px' }}>Pre Packed:</Text>
                        <Checkbox className={classes.inputElement} checked={prePacked} onChange={() => setPrePacked(!prePacked)} color={theme.colors.gray[9]} />
                        {packed.length > 0 && prePacked && <div className={classes.rowDiv}>
                            {packed.map((item: number, index: number) => (
                                <TextInput type='number' value={item} onChange={(e) => handlePrePackChanges(e, index)} />
                            ))}
                        </div>}
                    </div> */}
                    {FEATURE_FLAGS.OS_BOX_FLAG && <div className={classes.rowDiv} >
                        <Text className={classes.textWithInput} style={{ width: '100px' }}>Qty in box:</Text>
                        <Checkbox className={classes.inputElement} checked={osBox} onChange={(e) => handleOsBoxChange(e)} color={theme.colors.gray[9]} />
                        {osBoxPacked.length > 0 && osBox && <div className={classes.rowDiv}>
                            {osBoxPacked.map((item: number, index: number) => (
                                <TextInput type='number' value={item} onChange={(e) => handleOsPackedBoxChanges(e, index)} />
                            ))}
                        </div>}
                    </div>}
                    <div className={classes.rowDiv} >
                        <Text className={classes.textWithInput}>Stores:</Text>
                        <div style={{ flexGrow: 1 }} >
                            <Group className={classes.checkboxGroup}  >
                                <Checkbox color={theme.colors.gray[8]} defaultChecked value="checkall" label="Check All" onChange={(event) => handleCheckAll(event.currentTarget.checked)} />
                                {STORES.map((store) => <Checkbox
                                    color={theme.colors.gray[8]}
                                    key={store}
                                    label={STORES_PAIRS[store]}
                                    checked={stores.find((item) => item.store === store)?.checked}
                                    onChange={(event) => {
                                        setStores((prev) => prev.map((item) => item.store === store ? { ...item, checked: !item.checked } : item))
                                    }}
                                    value={store}
                                />)}
                            </Group>
                        </div>
                    </div>
                </div>
                <div>
                    <AllocationTable
                        stores={stores}
                        sizes={sizes}
                        totalProductionQuantities={totalProductionQuantities}
                        setTotalProductionQuantities={setTotalProductionQuantities}
                        standardQuantities={standardQuantities}
                        setStandardQuantities={setStandardQuantities}
                        tableData={tableData}
                        setTableData={setTableData}
                        calculateClicked={calculateClicked}
                        setCalculateClicked={setCalculateClicked}
                        calculateBtnClicked={calculateBtnClicked}
                        totalProductionSum={totalProductionSum}
                        setTotalProductionSum={setTotalProductionSum}
                        styleTableData={styleTableData}
                        prePacked={prePacked}
                        osBoxPacked={osBoxPacked}
                        osBox={osBox}
                        packed={packed}
                        requiredAdjustment={requiredAdjustment}
                    />
                </div>
                <div className={classes.buttonDiv}>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={handleCalculateClick} >Calculate</Button>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={() => handleReset()} >Reset</Button>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={onSave} >Save</Button>
                    <Button style={{ backgroundColor: theme.colors.gray[9] }} onClick={downloadAllPDFs} >Print</Button>
                </div>
            </div>
        </div>
    );
}

export default RestockCalculator;